/**
 * ExchangeNotificationContext - Exchange Marketplace Notifications
 *
 * Global context for Exchange notifications that persists across page navigations.
 * Tracks:
 * - New offers on your listings
 * - New bids on your auction listings
 * - Transaction updates (confirmations, completions)
 * - New comments on your listings
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { playNotificationSound, SoundType, initAudio } from '../utils/crewlinkAudio';

const ExchangeNotificationContext = createContext(null);

// Local storage key for persisting state
const STORAGE_KEY = 'exchange_notifications';

// Extended sound types for Exchange
export const ExchangeSoundType = {
    NEW_OFFER: 'new_offer',           // Someone made an offer on your listing
    NEW_BID: 'new_bid',               // Someone bid on your auction
    TRANSACTION_UPDATE: 'transaction_update', // Transaction status changed
    NEW_COMMENT: 'new_comment',       // New comment on your listing
    SALE_COMPLETE: 'sale_complete'    // Transaction completed
};

export const ExchangeNotificationProvider = ({ children }) => {
    const { currentUser, supabase, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState({
        offers: [],      // { listingId, offerId, fromUser, amount, timestamp }
        bids: [],        // { listingId, bidId, fromUser, amount, timestamp }
        transactions: [], // { transactionId, listingId, type, timestamp }
        comments: []     // { listingId, commentId, fromUser, timestamp }
    });
    const [hasUnread, setHasUnread] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const channelsRef = useRef([]);
    const audioInitialized = useRef(false);
    const userListingsRef = useRef(new Set()); // Cache of user's listing IDs

    // Initialize audio on first user interaction
    useEffect(() => {
        const handleInteraction = () => {
            if (!audioInitialized.current) {
                initAudio();
                audioInitialized.current = true;
            }
        };

        document.addEventListener('click', handleInteraction, { once: true });
        document.addEventListener('keydown', handleInteraction, { once: true });

        return () => {
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
        };
    }, []);

    // Load persisted state from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.notifications) {
                    setNotifications(parsed.notifications);
                }
            }
        } catch (err) {
            console.log('[ExchangeNotification] Error loading state:', err);
        }
    }, []);

    // Persist state to localStorage and update counts
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                notifications,
                timestamp: Date.now()
            }));
        } catch (err) {
            console.log('[ExchangeNotification] Error saving state:', err);
        }

        // Calculate total unread count
        const total =
            notifications.offers.length +
            notifications.bids.length +
            notifications.transactions.length +
            notifications.comments.length;

        setTotalCount(total);
        setHasUnread(total > 0);
    }, [notifications]);

    // Fetch user's active listings for filtering notifications
    const fetchUserListings = useCallback(async () => {
        if (!currentUser?.id || !supabase) return;

        try {
            const { data, error } = await supabase
                .from('marketplace_listings')
                .select('listing_id')
                .eq('user_id', currentUser.id)
                .in('status', ['active', 'pending']);

            if (!error && data) {
                userListingsRef.current = new Set(data.map(l => l.listing_id));
                console.log('[ExchangeNotification] User has', userListingsRef.current.size, 'active listings');
            }
        } catch (err) {
            console.error('[ExchangeNotification] Error fetching user listings:', err);
        }
    }, [currentUser?.id, supabase]);

    // Set up real-time subscriptions
    useEffect(() => {
        if (!isAuthenticated || !currentUser?.id) {
            return;
        }

        console.log('[ExchangeNotification] Setting up Exchange subscriptions for user:', currentUser.id);

        // First fetch user's listings
        fetchUserListings();

        // Channel for offers on user's listings
        const offersChannel = supabase
            .channel(`exchange-offers:${currentUser.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'marketplace_offers'
                },
                async (payload) => {
                    const offer = payload.new;

                    // Skip if the offer is from the current user
                    if (offer.from_user_id === currentUser.id) {
                        return;
                    }

                    // Check if this is for one of the user's listings
                    const { data: listing } = await supabase
                        .from('marketplace_listings')
                        .select('listing_id, user_id, title')
                        .eq('listing_id', offer.listing_id)
                        .single();

                    if (listing?.user_id !== currentUser.id) {
                        return;
                    }

                    // Fetch offerer's profile
                    const { data: profile } = await supabase
                        .from('profiles')
                        .select('display_name, username')
                        .eq('id', offer.from_user_id)
                        .single();

                    const fromUser = profile?.display_name || profile?.username || 'Unknown User';

                    console.log('[ExchangeNotification] New offer on listing:', listing.title);

                    // Play notification sound
                    playNotificationSound(SoundType.NEW_MESSAGE);

                    // Add to notifications
                    setNotifications(prev => ({
                        ...prev,
                        offers: [
                            ...prev.offers,
                            {
                                listingId: offer.listing_id,
                                listingTitle: listing.title,
                                offerId: offer.offer_id,
                                fromUser,
                                fromUserId: offer.from_user_id,
                                amount: offer.price_value,
                                offerType: offer.offer_type,
                                timestamp: Date.now()
                            }
                        ]
                    }));
                }
            )
            .subscribe();

        // Channel for bids on user's auction listings
        const bidsChannel = supabase
            .channel(`exchange-bids:${currentUser.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'marketplace_bids'
                },
                async (payload) => {
                    const bid = payload.new;

                    // Skip if the bid is from the current user
                    if (bid.bidder_user_id === currentUser.id) {
                        return;
                    }

                    // Check if this is for one of the user's listings
                    const { data: listing } = await supabase
                        .from('marketplace_listings')
                        .select('listing_id, user_id, title')
                        .eq('listing_id', bid.listing_id)
                        .single();

                    if (listing?.user_id !== currentUser.id) {
                        return;
                    }

                    // Fetch bidder's profile
                    const { data: profile } = await supabase
                        .from('profiles')
                        .select('display_name, username')
                        .eq('id', bid.bidder_user_id)
                        .single();

                    const fromUser = profile?.display_name || profile?.username || 'Unknown User';

                    console.log('[ExchangeNotification] New bid on auction:', listing.title);

                    // Play notification sound (use USER_JOINED for exciting bid sound)
                    playNotificationSound(SoundType.USER_JOINED);

                    // Add to notifications
                    setNotifications(prev => ({
                        ...prev,
                        bids: [
                            ...prev.bids,
                            {
                                listingId: bid.listing_id,
                                listingTitle: listing.title,
                                bidId: bid.bid_id,
                                fromUser,
                                fromUserId: bid.bidder_user_id,
                                amount: bid.bid_amount,
                                timestamp: Date.now()
                            }
                        ]
                    }));
                }
            )
            .subscribe();

        // Channel for transaction updates (as buyer or seller)
        const transactionsChannel = supabase
            .channel(`exchange-transactions:${currentUser.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*', // INSERT and UPDATE
                    schema: 'public',
                    table: 'marketplace_transactions'
                },
                async (payload) => {
                    const transaction = payload.new;
                    const isInsert = payload.eventType === 'INSERT';
                    const isUpdate = payload.eventType === 'UPDATE';

                    // Check if user is involved
                    const isBuyer = transaction.buyer_user_id === currentUser.id;
                    const isSeller = transaction.seller_user_id === currentUser.id;

                    if (!isBuyer && !isSeller) {
                        return;
                    }

                    // Determine notification type
                    let notificationType = null;
                    let message = '';

                    if (isInsert && isSeller) {
                        // New transaction created - seller notification
                        notificationType = 'new_sale';
                        message = 'New sale initiated!';
                    } else if (isUpdate) {
                        const oldStatus = payload.old?.status;
                        const newStatus = transaction.status;

                        if (oldStatus !== newStatus) {
                            if (newStatus === 'confirmed') {
                                notificationType = 'confirmed';
                                message = 'Transaction confirmed by both parties!';
                            } else if (newStatus === 'completed') {
                                notificationType = 'completed';
                                message = 'Transaction completed!';
                            }
                        } else {
                            // Check for confirmation updates
                            if (isSeller && transaction.buyer_confirmed && !payload.old?.buyer_confirmed) {
                                notificationType = 'buyer_confirmed';
                                message = 'Buyer confirmed the transaction!';
                            } else if (isBuyer && transaction.seller_confirmed && !payload.old?.seller_confirmed) {
                                notificationType = 'seller_confirmed';
                                message = 'Seller confirmed the transaction!';
                            }
                        }
                    }

                    if (!notificationType) {
                        return;
                    }

                    // Get listing title
                    const { data: listing } = await supabase
                        .from('marketplace_listings')
                        .select('title')
                        .eq('listing_id', transaction.listing_id)
                        .single();

                    console.log('[ExchangeNotification] Transaction update:', notificationType, listing?.title);

                    // Play appropriate sound
                    if (notificationType === 'completed') {
                        playNotificationSound(SoundType.SUCCESS);
                    } else {
                        playNotificationSound(SoundType.NEW_MESSAGE);
                    }

                    // Add to notifications
                    setNotifications(prev => ({
                        ...prev,
                        transactions: [
                            ...prev.transactions,
                            {
                                transactionId: transaction.transaction_id,
                                listingId: transaction.listing_id,
                                listingTitle: listing?.title || 'Unknown Listing',
                                type: notificationType,
                                message,
                                isBuyer,
                                isSeller,
                                timestamp: Date.now()
                            }
                        ]
                    }));
                }
            )
            .subscribe();

        // Channel for comments on user's listings
        const commentsChannel = supabase
            .channel(`exchange-comments:${currentUser.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'marketplace_comments'
                },
                async (payload) => {
                    const comment = payload.new;

                    // Skip if the comment is from the current user
                    if (comment.user_id === currentUser.id) {
                        return;
                    }

                    // Check if this is for one of the user's listings
                    const { data: listing } = await supabase
                        .from('marketplace_listings')
                        .select('listing_id, user_id, title')
                        .eq('listing_id', comment.listing_id)
                        .single();

                    if (listing?.user_id !== currentUser.id) {
                        return;
                    }

                    // Fetch commenter's profile
                    const { data: profile } = await supabase
                        .from('profiles')
                        .select('display_name, username')
                        .eq('id', comment.user_id)
                        .single();

                    const fromUser = profile?.display_name || profile?.username || 'Unknown User';

                    console.log('[ExchangeNotification] New comment on listing:', listing.title);

                    // Play notification sound
                    playNotificationSound(SoundType.NEW_MESSAGE);

                    // Add to notifications
                    setNotifications(prev => ({
                        ...prev,
                        comments: [
                            ...prev.comments,
                            {
                                listingId: comment.listing_id,
                                listingTitle: listing.title,
                                commentId: comment.comment_id,
                                fromUser,
                                fromUserId: comment.user_id,
                                preview: comment.comment_text?.substring(0, 50) + (comment.comment_text?.length > 50 ? '...' : ''),
                                timestamp: Date.now()
                            }
                        ]
                    }));
                }
            )
            .subscribe();

        channelsRef.current = [offersChannel, bidsChannel, transactionsChannel, commentsChannel];

        return () => {
            console.log('[ExchangeNotification] Cleaning up subscriptions');
            channelsRef.current.forEach(channel => {
                supabase.removeChannel(channel);
            });
            channelsRef.current = [];
        };
    }, [isAuthenticated, currentUser?.id, supabase, fetchUserListings]);

    // Clear offer notifications for a listing
    const clearOfferNotifications = useCallback((listingId) => {
        setNotifications(prev => ({
            ...prev,
            offers: listingId
                ? prev.offers.filter(n => n.listingId !== listingId)
                : []
        }));
    }, []);

    // Clear bid notifications for a listing
    const clearBidNotifications = useCallback((listingId) => {
        setNotifications(prev => ({
            ...prev,
            bids: listingId
                ? prev.bids.filter(n => n.listingId !== listingId)
                : []
        }));
    }, []);

    // Clear transaction notifications
    const clearTransactionNotifications = useCallback((transactionId) => {
        setNotifications(prev => ({
            ...prev,
            transactions: transactionId
                ? prev.transactions.filter(n => n.transactionId !== transactionId)
                : []
        }));
    }, []);

    // Clear comment notifications for a listing
    const clearCommentNotifications = useCallback((listingId) => {
        setNotifications(prev => ({
            ...prev,
            comments: listingId
                ? prev.comments.filter(n => n.listingId !== listingId)
                : []
        }));
    }, []);

    // Clear all notifications
    const clearAllNotifications = useCallback(() => {
        setNotifications({
            offers: [],
            bids: [],
            transactions: [],
            comments: []
        });
    }, []);

    // Clear notifications for a specific listing (all types)
    const clearListingNotifications = useCallback((listingId) => {
        setNotifications(prev => ({
            offers: prev.offers.filter(n => n.listingId !== listingId),
            bids: prev.bids.filter(n => n.listingId !== listingId),
            transactions: prev.transactions.filter(n => n.listingId !== listingId),
            comments: prev.comments.filter(n => n.listingId !== listingId)
        }));
    }, []);

    // Get notifications for a specific listing
    const getListingNotifications = useCallback((listingId) => {
        return {
            offers: notifications.offers.filter(n => n.listingId === listingId),
            bids: notifications.bids.filter(n => n.listingId === listingId),
            comments: notifications.comments.filter(n => n.listingId === listingId)
        };
    }, [notifications]);

    const value = {
        notifications,
        hasUnread,
        totalCount,
        clearOfferNotifications,
        clearBidNotifications,
        clearTransactionNotifications,
        clearCommentNotifications,
        clearAllNotifications,
        clearListingNotifications,
        getListingNotifications,
        // Re-export sound functions for convenience
        playNotificationSound,
        SoundType
    };

    return (
        <ExchangeNotificationContext.Provider value={value}>
            {children}
        </ExchangeNotificationContext.Provider>
    );
};

export const useExchangeNotifications = () => {
    const context = useContext(ExchangeNotificationContext);
    if (!context) {
        throw new Error('useExchangeNotifications must be used within an ExchangeNotificationProvider');
    }
    return context;
};

export default ExchangeNotificationContext;
